import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';

import {ThiemeCatalogEntry, ThiemeCatalogIncompatibleRemote} from '../../../types';
import {configKeys} from '../thieme-catalog.config-keys';
import {AuthUriService} from './auth-uri.service';


/**
 * Api access to the thieme connector of the instance: catalog listing
 * (server side filtering, sorting, pagination) and mqt download.
 */
@Injectable()
export class ThiemeCatalogService {

  public constructor(
    private http: HttpClient,
    private authUriService: AuthUriService) {
  }

  /**
   * Loads catalog entries from the thieme connector. Filter and pagination
   * run server side against the mirrored catalog collection. incompatibleRemote
   * is set when a newer central delivery exists that this instance can not
   * mirror because it requires an unsupported questionnaire schema version.
   *
   * @param {any} filter mongo style query
   * @param {{limit?: number, offset?: number, sort?: string}} opts
   * @returns {Promise<{items: ThiemeCatalogEntry[], total: number, version: string,
   *   incompatibleRemote: ThiemeCatalogIncompatibleRemote}>}
   */
  public getCatalog(filter?: any, opts?: any): Promise<{
    items: ThiemeCatalogEntry[], total: number, version: string, incompatibleRemote: ThiemeCatalogIncompatibleRemote
  }> {
    const baseUri = this.authUriService.buildUrl(configKeys.THIEME_CONNECTOR_URI_CONFIG_KEY, '/catalog');
    const headers = this.authUriService.getAuthHeaders();

    const query: any = {filter: JSON.stringify(filter || {})};
    opts && opts.limit && (query.limit = `${opts.limit}`);
    opts && opts.offset && (query.offset = `${opts.offset}`);
    opts && opts.sort && (query.sort = opts.sort);

    const basePath = `${baseUri}?${decodeURI(new URLSearchParams(query).toString())}`;

    return new Promise((resolve, reject) => {
      this.http.get(basePath, {headers})
        .subscribe((response: any) => {
            resolve({
              items: response.items || [],
              total: response.total || 0,
              version: response.version || null,
              incompatibleRemote: response.incompatibleRemote || null
            });
          },
          error => {
            reject(error);
          });
    });
  }

  /**
   * Downloads the mqt archive of the given sap id through the thieme connector.
   *
   * @param {string} sapId
   * @returns {Promise<Blob>}
   */
  public downloadMqt(sapId: string): Promise<Blob> {
    const basePath = this.authUriService.buildUrl(
      configKeys.THIEME_CONNECTOR_URI_CONFIG_KEY, `/catalog/${encodeURIComponent(sapId)}/mqt`
    );
    const headers = this.authUriService.getAuthHeaders();

    return new Promise((resolve, reject) => {
      this.http.get(basePath, {headers, responseType: 'blob'})
        .subscribe(
          (blob: Blob) => resolve(blob),
          error => reject(error));
    });
  }
}
